import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  todayISO,
  parseISO,
  calendarYearPath,
  calendarMonthPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import { withSlash, absUrl } from "@/lib/url";
import { SUPPORTED_YEARS } from "@/data/holidays";

const CANONICAL = "https://hari.co.id/en/calendar/";

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const Route = createFileRoute("/en/calendar/")({
  head: () => ({
    meta: [
      { title: "Indonesia Calendar - Year, Month & Date Hub" },
      {
        name: "description",
        content:
          "Indonesia calendar hub: pick a year, month, or date for public holidays, joint leave, Javanese weton, and Hijri.",
      },
      { property: "og:title", content: "Indonesia Calendar - Year, Month & Date" },
      { property: "og:description", content: "Pick a year, month, or date for the full Indonesia calendar." },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "id_ID" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: EnCalendarIndexPage,
});

function EnCalendarIndexPage() {
  return (
    <SiteShell locale="en">
      <Body />
    </SiteShell>
  );
}

function Body() {
  const { href } = useLocale();
  const today = parseISO(todayISO())!;
  const cy = today.y;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: absUrl(href("/")) },
      { "@type": "ListItem", position: 2, name: "Calendar", item: absUrl(withSlash(href("/kalender"))) },
    ],
  };
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">Home</Link> / Calendar
      </nav>
      <header className="mb-6">
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">Indonesia Calendar</h1>
        <p className="mt-2 text-muted-foreground">
          Pick a year, month, or date for the full Indonesia calendar with public holidays, joint leave, Javanese weton, and Hijri.
        </p>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Today & quick links</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to={href(calendarDatePath(today.y, today.m, today.d)) as never} className="rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary hover:bg-primary/15">
            Today, {today.d} {MONTH_NAMES_EN[today.m - 1]} {today.y}
          </Link>
          <Link to={href(calendarMonthPath(today.y, today.m)) as never} className="rounded-md bg-muted px-3 py-1.5 hover:bg-accent">
            This month ({MONTH_NAMES_EN[today.m - 1]} {today.y})
          </Link>
          <Link to={href(calendarYearPath(today.y)) as never} className="rounded-md bg-muted px-3 py-1.5 hover:bg-accent">
            This year ({today.y})
          </Link>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Months of {cy}</h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {months.map((m) => (
            <Link key={m} to={href(calendarMonthPath(cy, m)) as never}
              className={"rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-accent " + (m === today.m ? "ring-1 ring-primary text-primary" : "")}>
              {MONTH_NAMES_EN[m - 1]} {cy}
            </Link>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Years</h2>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_YEARS.map((y) => (
            <Link key={y} to={href(calendarYearPath(y)) as never}
              className={"rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent " + (y === cy ? "bg-primary/10 font-semibold text-primary" : "")}>
              {y}
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}